/**
 * Return-URL helpers for the interaction-auth-gate flow.
 *
 * A mirrored copy of `frontend/src/lib/guestMode/returnUrl.ts` lives in the
 * shared layer so the auth-gating dialog (also in shared) can build login
 * URLs without depending on `frontend/src`. Behaviour and contract are
 * intentionally identical so the two never diverge.
 */

const NEXT_PARAM = "next";
const SUPPORTED_LOCALES = new Set(["en", "ar", "de", "es", "fr"]);

export function isSafeReturnPath(path: string): boolean {
  if (typeof path !== "string") return false;
  if (!path.startsWith("/")) return false;
  if (path.startsWith("//")) return false;
  if (path.includes("://")) return false;
  return true;
}

export function buildLoginUrlPreservingReturn(
  returnTo?: string,
  destination: "login" | "register" = "login",
): string {
  const target = resolveReturnTarget(returnTo);
  const base = localizedAuthPath(target, destination);
  if (!target) return base;
  const params = new URLSearchParams();
  params.set(NEXT_PARAM, target);
  return `${base}?${params.toString()}`;
}

export function localizedAuthPath(
  returnTo?: string | null,
  destination: "login" | "register" = "login",
): string {
  const locale = localeFromPath(returnTo);
  return locale ? `/${locale}/${destination}` : `/${destination}`;
}

export function localizedRootPath(path?: string | null): string {
  const locale = localeFromPath(path);
  return locale ? `/${locale}` : "/";
}

function localeFromPath(path?: string | null): string | null {
  if (!path || !isSafeReturnPath(path)) return null;
  const firstSegment = path.split(/[/?#]/)[1];
  return firstSegment && SUPPORTED_LOCALES.has(firstSegment) ? firstSegment : null;
}

function resolveReturnTarget(explicit?: string): string | null {
  if (explicit !== undefined) {
    return isSafeReturnPath(explicit) ? explicit : null;
  }
  if (typeof window === "undefined") return null;
  const path = window.location.pathname + window.location.search;
  return isSafeReturnPath(path) ? path : null;
}
